import React, { useEffect, useContext } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { GlobalStateContext } from "../../controller/GlobalState";
import { blogPosts } from "../../controller/global";
import ExploreOtherCaseStudy from "../../component/ExploreOtherCaseStudy";

const QUICK_FACTS = [
  { value: "ASP.NET Core", label: "Backend Framework" },
  { value: "MongoDB", label: "Primary Data Store" },
  { value: "4", label: "Core Platform Pillars" },
  { value: "US", label: "Construction Industry" },
];

const PILLARS = [
  {
    title: "Powerful Project Management",
    desc: "A configurable project management environment that adapts to the unique needs of every construction project, regardless of complexity or scale.",
  },
  {
    title: "End-to-End Transparency",
    desc: "Project transparency for all stakeholders - owners, contractors, lenders, and title companies - with clear, auditable views of project finances and obligations in real time.",
  },
  {
    title: "Built-in Protections",
    desc: "Integrated protections that reduce fraud and payment risk, ensuring each transaction is secure, documented, and traceable.",
  },
  {
    title: "Paperless Workflows",
    desc: "Entirely paperless workflows that cut administrative overhead, replacing spreadsheets and email chains with a single trusted environment.",
  },
];

const KEY_IMPACT = [
  "Eliminated payment disputes through transparent, real-time visibility",
  "Reduced administrative overhead with fully paperless workflows",
  "Built trust between all project stakeholders with auditable transactions",
  "Minimized payment risk through secure escrow management",
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const ThreeLockBox = () => {
  const caseStudy = blogPosts.find((post) => post.slug === "three-lock-box");
  const { setIsOpen } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Three Lock Box Case Study | InQmise Technologies";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Discover how we built Three Lock Box - a secure, paperless escrow and payment platform for the US construction industry.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies/three-lock-box");
  }, []);

  const openContactModal = () => setIsOpen(true);
  const goToCaseStudies = (e) => { e.preventDefault(); navigate("/case-studies"); };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header
        style={caseStudy?.image ? {
          backgroundImage: `linear-gradient(rgba(251,252,248,.5),rgba(251,252,248,.8)), url(${caseStudy.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="ea-hero ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href="/case-studies" onClick={goToCaseStudies}>← All Case Studies</a>
            <p className="ea-eyebrow">Case study · {caseStudy?.category}</p>
            <h1>Three Lock Box: secure construction finance and escrow software</h1>
            <p className="ea-lede">
              Building a transparent, paperless escrow and payment platform for the US construction
              industry.
            </p>
            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
              <a className="ea-text-link" href="/case-studies" onClick={goToCaseStudies}>Explore other case studies</a>
            </div>
          </Reveal>

          <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
            <div className="ea-engine-top"><span /> THREE LOCK BOX / QUICK FACTS <b>LIVE</b></div>
            {QUICK_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </header>

      {/* THE CHALLENGE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The challenge</p>
          <h2 className="cd-h2">The Challenge</h2>
          <div className="std-overview">
            <p>
              Construction projects are notoriously prone to payment disputes, opaque workflows, and
              mountains of paperwork. Project owners, contractors, subcontractors, and lenders often lack a
              single source of truth for commitments and disbursements, creating costly delays and eroding
              trust between parties. Three Lock Box set out to change this with a fully digital, secure escrow
              and payment control environment purpose-built for the construction sector.
            </p>
          </div>
        </Reveal>
      </section>

      {/* WHY CONSTRUCTION PAYMENTS ARE HARD */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The domain</p>
            <h2 className="cd-h2">Why Construction Payments Are Harder Than They Look</h2>
            <div className="std-overview">
              <p>
                Most software handles payments as a single event: an amount, a payer, a payee, a
                timestamp. Construction does not work that way, and any platform that assumes it does
                will fail on its first real project.
              </p>
              <p>
                A commercial build is financed through a <strong>draw schedule</strong> &mdash; the money is
                released in stages as work is completed, not up front. Each draw requires someone to
                certify that the work it pays for actually exists. A lender will not release funds
                without that certification, an owner will not certify without inspection, and a general
                contractor cannot pay subcontractors until the draw clears. Every party is waiting on
                a different party, and the paperwork proving each step typically lives in email.
              </p>
              <p>
                Layered on top of that is <strong>retainage</strong>: a percentage of every payment,
                commonly five to ten percent, withheld until the project completes. It is tracked
                separately from the payment it was withheld from, released on its own schedule, and is
                a frequent source of dispute months after the work was done.
              </p>
              <p>
                Then there is lien risk. In the United States, a subcontractor who has not been paid
                can file a <strong>mechanics lien</strong> against the property &mdash; including when the
                general contractor was paid and simply did not pass the money down. Owners and title
                companies protect against this with <strong>lien waivers</strong>, signed at each payment,
                which have to be collected from every party in the chain and matched to the specific
                payment they cover. Miss one, and a property that everyone believed was clear is not.
              </p>
              <p>
                This is why construction payment disputes are so common, and why they are rarely about
                the money itself. They are about whether a particular piece of work was certified, by
                whom, on what date, and whether the waiver covering it was ever signed. Spreadsheets and
                email threads cannot answer those questions reliably months later. That is the problem
                Three Lock Box exists to solve.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT THAT MEANS FOR THE SOFTWARE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The engineering problem</p>
          <h2 className="cd-h2">What Escrow Demands of the Software</h2>
          <div className="std-overview">
            <p>
              Holding other people&rsquo;s money in escrow changes the engineering requirements in ways that
              a normal CRUD application never has to contend with.
            </p>
            <p>
              <strong>A payment is a state machine, not a record.</strong> A draw moves through requested,
              certified, approved, funded and released, and different parties hold the authority to
              advance it at different stages. Conditional release means the platform has to encode which
              conditions apply to which draw, evaluate them, and refuse to advance when they are not met
              &mdash; rather than leaving that judgment to whoever happens to be looking at the screen.
            </p>
            <p>
              <strong>Every figure has to be explainable after the fact.</strong> When a dispute surfaces
              six months later, the question is never just &ldquo;what is the balance&rdquo;. It is &ldquo;how did it
              get there, who approved each step, and what did they see at the time&rdquo;. That means an
              append-only history rather than mutable records: a corrected certification is a new
              event that supersedes the old one, not an overwrite that erases it.
            </p>
            <p>
              <strong>Documents are part of the transaction, not attachments to it.</strong> A lien waiver
              is not a file someone uploaded; it is evidence tied to a specific payment to a specific
              party for a specific scope of work. The link between the money and the paperwork is the
              entire point, and it has to survive the project it belongs to.
            </p>
            <p>
              <strong>Different parties must see different truths of the same project.</strong> A lender
              needs disbursement status and remaining commitment. A subcontractor needs to know whether
              their invoice cleared. An owner needs the whole picture. A title company needs the waiver
              chain. One dataset, four legitimate and quite different views of it &mdash; which is why
              transparency here means role-appropriate visibility, not simply showing everyone
              everything.
            </p>
          </div>
        </Reveal>
      </section>

      {/* OUR SOLUTION */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Our solution</p>
            <h2 className="cd-h2">Our Solution</h2>
            <div className="std-overview">
              <p>
                We designed and delivered a full-stack web platform from the ground up using ASP.NET Core
                Razor Pages backed by a .NET Core API layer and MongoDB as the primary data store. This
                modern, cloud-ready stack enabled rapid iteration on complex domain logic — multi-party
                escrow flows, conditional payment releases, document management, and real-time project
                dashboards — while keeping the interface clean and approachable for non-technical
                construction professionals.
              </p>
              <p>
                MongoDB's flexible document model was a natural fit for representing highly variable
                construction project structures, where no two projects share the same mix of participants, draw
                schedules, or contractual conditions. The .NET Core API provided a robust, testable service
                layer, while Razor Pages delivered server-rendered, SEO-friendly views without the overhead of
                a separate front-end framework — keeping the team focused on business value rather than
                infrastructure.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STACK RATIONALE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Technology choices</p>
          <h2 className="cd-h2">Why This Stack, And What We Traded Away</h2>
          <div className="std-overview">
            <p>
              <strong>MongoDB over a relational store.</strong> The instinct for a financial system is a
              relational database, and for the ledger itself that instinct is right. But construction
              projects have no stable shape: a residential build with one lender and three subcontractors
              and a hospital expansion with staged financing, dozens of trades and a title company share
              almost no structure. Modeling that relationally means either a table with many nullable
              columns or an entity-attribute-value design that is painful to query. A document model
              represents each project as it actually is. The trade-off is real &mdash; the schema
              discipline a relational store enforces for you has to be enforced in application code
              instead, deliberately rather than by default.
            </p>
            <p>
              <strong>Razor Pages over a single-page front end.</strong> A React or Angular front end would
              have been the fashionable choice and the wrong one here. This platform is used by
              construction professionals, on site, on whatever device and connection they have, for
              tasks that are form-driven rather than interactive. Server-rendered pages load faster on a
              poor connection, keep the security-sensitive logic on the server, and remove an entire
              build pipeline and API contract from a small team&rsquo;s maintenance burden. What we gave up is
              the fluid, app-like interaction model &mdash; which this audience was not asking for.
            </p>
            <p>
              <strong>A separate .NET Core API layer.</strong> The page layer and the service layer were
              kept distinct even though Razor Pages does not require it. Escrow logic is the part of this
              system that must be tested exhaustively, and it is far easier to test a service boundary
              than a page handler. It also means the same logic can be driven by something other than a
              web page later &mdash; a lender integration, a mobile client &mdash; without being rewritten.
            </p>
          </div>
        </Reveal>
      </section>

      {/* CORE PILLARS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Core pillars</p>
          <h2 className="cd-h2">Core Pillars</h2>
        </Reveal>
        <div className="re-numbered-list">
          {PILLARS.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-numbered-row" key={item.title}>
              <div className="re-numbered-row-num">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUTCOMES & IMPACT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Outcomes & impact</p>
            <h2 className="cd-h2">Outcomes & Impact</h2>
            <div className="std-overview">
              <p>
                Three Lock Box launched as a production-ready platform serving US-based construction clients,
                delivering on its four core pillars: a powerful and configurable project management environment,
                end-to-end project transparency for all stakeholders, built-in protections that reduce fraud and
                payment risk, and entirely paperless workflows that cut administrative overhead. The platform
                gives every participant — owner, contractor, lender, or title company — a clear, auditable view
                of project finances and obligations in real time, replacing spreadsheets and email chains with a
                single trusted environment.
              </p>
            </div>
            <p style={{ margin: "24px 0 12px", fontFamily: "Syne, sans-serif", fontSize: "var(--t-sm)", fontWeight: 700, letterSpacing: "-.03em" }}>Key Impact</p>
            <ul className="re-bare-list">
              {KEY_IMPACT.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE OTHER CASE STUDIES */}
      <ExploreOtherCaseStudy currentSlug="three-lock-box" />

      {/* CTA */}
      <section className="ea-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Ready when you are</div>
            <h2 style={{ margin: "16px 0" }}>Ready to Transform Your Business?</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              Let's discuss how we can build sophisticated solutions for your most complex problems.
            </p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Get in Touch</button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ThreeLockBox;
